// Organization Edit Form - Mobile modal for editing organizations with tabs
import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { IOrganization, IEntrataUser } from '@/types/admin/organizations';
import { ORGANIZATION_TABS } from '@/constants/admin';
import { OrganizationFormContent } from './OrganizationFormContent';
import { OrganizationPropertiesTab } from './desktop/OrganizationPropertiesTab';
import { OrganizationUsersTab } from './desktop/OrganizationUsersTab';
import { 
  useLazyGetOrganizationPropertiesQuery,
  useLazyGetOrganizationAdminsQuery,
} from '@/store/api/admin/organizationsApi';

interface OrganizationEditFormProps {
  organization: IOrganization;
  formData: {
    name: string;
    domain: string;
    paymentAccounts: any[];
    entrataUsers: IEntrataUser[];
  };
  errors: {
    name: string;
    domain: string;
  };
  isLoadingConfigs: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isPatchingConfig: boolean;
  onNameChange: (text: string) => void;
  onDomainChange: (text: string) => void;
  onEntrataUsersChange: (users: IEntrataUser[]) => void;
  onPaymentAccountChange: (index: number, accountNumber: string) => void;
  onUpdate: () => void;
  onDelete: () => void;
}

export const OrganizationEditForm: React.FC<OrganizationEditFormProps> = ({
  organization,
  formData,
  errors,
  isLoadingConfigs,
  isUpdating,
  isDeleting,
  isPatchingConfig,
  onNameChange,
  onDomainChange,
  onEntrataUsersChange,
  onPaymentAccountChange,
  onUpdate,
  onDelete,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  // Tab data queries
  const [getProperties, { data: propertiesData, isLoading: isLoadingProperties }] = 
    useLazyGetOrganizationPropertiesQuery();
  const [getAdmins, { data: adminsData, isLoading: isLoadingAdmins }] = 
    useLazyGetOrganizationAdminsQuery();

  // Handle tab change
  const handleTabChange = useCallback((index: number) => {
    setActiveTab(index);
    if (index === 1) {
      getProperties(organization.id);
    } else if (index === 2) {
      getAdmins(organization.id);
    }
  }, [organization.id, getProperties, getAdmins]);

  const isLoading = isUpdating || isPatchingConfig;

  return (
    <>
      {/* Tabs */}
      <View style={styles.tabs}>
        {ORGANIZATION_TABS.map((tab, index) => (
          <TouchableOpacity
            key={tab}
            style={[styles.tab, activeTab === index && styles.activeTab]}
            onPress={() => handleTabChange(index)}
          >
            <Text style={[styles.tabText, activeTab === index && styles.activeTabText]}>
              {tab}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Tab Content */}
      <View style={styles.tabContent}>
        {activeTab === 0 && (
          <>
            <OrganizationFormContent
              formData={formData}
              errors={errors}
              isLoadingConfigs={isLoadingConfigs}
              isDisabled={isLoading}
              onNameChange={onNameChange}
              onDomainChange={onDomainChange}
              onEntrataUsersChange={onEntrataUsersChange}
              onPaymentAccountChange={onPaymentAccountChange}
            />

            {/* Action Buttons for Edit */}
            <View style={styles.editActions}>
              <TouchableOpacity
                style={styles.deleteButton}
                onPress={onDelete}
                disabled={isDeleting || isUpdating}
              >
                {isDeleting ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.deleteButtonText}>Delete</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.saveButton, isLoading && styles.buttonDisabled]}
                onPress={onUpdate}
                disabled={isLoading || isDeleting}
              >
                {isLoading ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.saveButtonText}>Save Changes</Text>
                )}
              </TouchableOpacity>
            </View>
          </>
        )}

        {activeTab === 1 && (
          <OrganizationPropertiesTab
            organizationId={organization.id}
            properties={propertiesData?.response || []}
            isLoading={isLoadingProperties}
          />
        )}

        {activeTab === 2 && (
          <OrganizationUsersTab
            organizationId={organization.id}
            admins={adminsData?.response || []}
            isLoading={isLoadingAdmins}
          />
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  tabs: {
    flexDirection: 'row',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    marginHorizontal: 16,
    marginVertical: 12,
    borderRadius: 8,
    padding: 4,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
  activeTab: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  tabText: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  activeTabText: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  tabContent: {
    flex: 1,
  },
  editActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 24,
    gap: 12,
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND,
  },
  saveButtonText: {
    color: ADMIN_COLORS.BUTTON.PRIMARY_TEXT,
    fontSize: 14,
    fontWeight: '600',
  },
  deleteButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#DC2626',
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});

